// ════════════════════════════════════════════════════════════════════════════
// AdminPage — shell del panel de administración (/admin).
//
// Aquí solo viven la comprobación de rol, el estado compartido entre pestañas
// (usuarios, noticias, avisos) y el cambio de pestaña. Cada pestaña está en
// pages/admin/tabs/ y las constantes comunes en pages/admin/shared.jsx.
// ════════════════════════════════════════════════════════════════════════════
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle } from 'lucide-react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageWrapper from '../components/PageWrapper';
import SEOMeta from '../components/SEOMeta';
import { AdminSidebar, AdminTopbar, AdminDashboard } from '../components/AdminConsole';
import { BACKEND_URL, apiErr } from './admin/shared';
import NoticiasTab from './admin/tabs/NoticiasTab';
import UsuariosTab from './admin/tabs/UsuariosTab';
import ShopTab from './admin/tabs/TiendaTab';
import ComprasTab from './admin/tabs/ComprasTab';
import AddonsTab from './admin/tabs/AddonsTab';
import ServidorTab from './admin/tabs/ServidorTab';
import MapaTab from './admin/tabs/MapaTab';
import CuentaTab from './admin/tabs/CuentaTab';
import EncuestaAdminTab from './admin/tabs/EncuestaTab';
import ReferralsAdminTab from './admin/tabs/ReferidosTab';
import DevToolsTab from './admin/tabs/DevTab';
import '../admin-console.css';

export default function AdminPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('dashboard');
  const [mapRefreshTick, setMapRefreshTick] = useState(0);
  const [statsTargetUser, setStatsTargetUser] = useState(null);
  const goToPlayerStats = (username) => { setStatsTargetUser(username); setTab('estadisticas'); };
  const [devCrystals, setDevCrystals] = useState(null);
  const [user, setUser] = useState(null);
  const [news, setNews] = useState([]);
  const [users, setUsers] = useState([]);
  const [feedback, setFeedback] = useState(null); // { type: 'ok'|'err', msg }

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true })
      .then(({ data }) => {
        if (data.role !== 'admin') navigate('/');
        else setUser(data);
      })
      .catch(() => navigate('/login'));
  }, [navigate]);

  const fetchNews = () =>
    axios.get(`${BACKEND_URL}/api/admin/news`, { withCredentials: true })
      .then(({ data }) => setNews(data))
      .catch((err) => showFeedback('err', apiErr(err, 'No se pudieron cargar las noticias.')));

  const fetchUsers = () =>
    axios.get(`${BACKEND_URL}/api/admin/users`, { withCredentials: true })
      .then(({ data }) => setUsers(data))
      .catch((err) => showFeedback('err', apiErr(err, 'No se pudieron cargar los usuarios.')));

  useEffect(() => { if (user) { fetchNews(); fetchUsers(); } }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const showFeedback = (type, msg) => {
    setFeedback({ type, msg });
    setTimeout(() => setFeedback(null), 3500);
  };

  if (!user) return null;

  const onlineCount = users.filter((u) => u.is_online).length;

  return (
    <PageWrapper scrollable>
      <SEOMeta title="Panel de administración" noIndex noFollow />
      <Navbar />
      <main className="admin-main adc-shell">
        <AdminSidebar tab={tab} setTab={setTab} usersCount={users.length} username={user.username} />
        <div className="adc-main">
          <AdminTopbar
            tab={tab}
            onlineCount={onlineCount}
            username={user.username}
            onRefresh={() => { fetchNews(); fetchUsers(); setMapRefreshTick((n) => n + 1); }}
          />

          {/* Feedback */}
          {feedback && (
            <div className={`admin-feedback ${feedback.type === 'ok' ? 'admin-feedback--ok' : 'admin-feedback--err'}`}
              data-testid="admin-feedback">
              {feedback.type === 'ok' ? <CheckCircle size={15} /> : <AlertCircle size={15} />}
              <span>{feedback.msg}</span>
            </div>
          )}

          <div className="adc-content">
            {tab === 'dashboard' && (
              <AdminDashboard news={news} users={users} username={user.username} onNavigate={setTab} onOpenAccount={goToPlayerStats} />
            )}
            {tab === 'noticias' && (
              <NoticiasTab news={news} setNews={setNews} fetchNews={fetchNews} showFeedback={showFeedback} />
            )}
            {tab === 'usuarios' && (
              <UsuariosTab users={users} fetchUsers={fetchUsers} showFeedback={showFeedback} onViewStats={goToPlayerStats} />
            )}
            {tab === 'tienda' && <ShopTab showFeedback={showFeedback} />}
            {tab === 'compras' && <ComprasTab onViewStats={goToPlayerStats} />}
            {tab === 'addons' && <AddonsTab showFeedback={showFeedback} />}
            {tab === 'servidor' && <ServidorTab showFeedback={showFeedback} onlineCount={onlineCount} />}
            {tab === 'mapa' && <MapaTab refreshTick={mapRefreshTick} />}
            {tab === 'estadisticas' && (
              <CuentaTab
                presetUsername={statsTargetUser}
                onConsumePreset={() => setStatsTargetUser(null)}
                showFeedback={showFeedback}
              />
            )}
            {tab === 'encuesta' && <EncuestaAdminTab />}
            {tab === 'referidos' && <ReferralsAdminTab />}
            {tab === 'dev' && (
              <DevToolsTab
                user={{ ...user, frost_crystals: devCrystals ?? user?.frost_crystals ?? 0 }}
                onCrystalsChange={setDevCrystals}
              />
            )}
          </div>{/* .adc-content */}
        </div>{/* .adc-main */}
      </main>
      <Footer />
    </PageWrapper>
  );
}
